type PIMItemStatus = 'local' | 'new' | 'review' | 'deleted' | 'active' | 'archived';

export function getItemSlugFromNameAndId(name: string, id: number): string {
  const slugifiedName = name?.trim()?.replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .replace(/-+/g, '-')
    .toLowerCase() || "";
  return `${slugifiedName}-${id || 0}`
}

export interface BreadcrumbItem {
  id: number;
  name: string;
  slug: string;
  href: string;
  isCurrent?: boolean;
}

export interface PIMIndexItem {
  pk: string;
  itemId: number;
  ItemCosmosUUID: string;
  _CATEGORY: number;
  GPID: string;
  Name: string;
  Description: string;
  Active: boolean;
  IsDeleted: boolean;
  AvailableQty: number | null;
  Retail: number | null;
  cpTotalValue: number;
  cpHasImages: boolean;
}

/**
 * Minimal projection of a PIMItem for the catalog grid — exactly the fields
 * the card renders. Built server-side by src/lib/item-card-dto.ts; the full
 * PIMItem never crosses to the client on grid surfaces. `Retail` is present
 * only on responses rendered for authenticated employees; Wholesale/Cost are
 * never included.
 */
export interface ItemCardDTO {
  /** Cosmos document id — used for dev-mode duplicate detection in the grid. */
  id: string;
  Id: number;
  GPID: string;
  Name: string;
  Description: string | null;
  /** Leaf category name shown on the card badge. */
  categoryName: string | null;
  /** Root category slug for building the item link. */
  rootCategorySlug: string | null;
  /** c._CATEGORY — fallback key into rootCategories for link building. */
  categoryId: number | null;
  /** Primary image URL (lowest Priority), pre-sorted server-side. */
  imageUrl: string | null;
  condition: string | null;
  /** Year attribute value, set only when the category shows it in the name. */
  year: string | null;
  /** Warehouse display names with stock, pre-derived server-side. */
  locations: string[];
  AvailableQty: number | null;
  // NOTE: no pricing fields, for anyone — card payloads are identical for all
  // visitors (edge-cacheable); employees overlay prices client-side from the
  // session-gated /api/items/pricing endpoint.
}

export interface PIMItem extends PIMIndexItem {
  id: string;
  Id: number | null;
  MondayId: string | null;
  LotNo: string | null;
  SerialNo: string | null;
  SKU: string | null;
  UPC: string | null;
  Wholesale: number | null;
  Cost: number | null;
  CreatedOn: string | null;
  MergedOn: string | null;
  WebLink: string | null;
  ExternalSystems: any | null;
  _IMAGES: IMAGE[];
  _ATTRIBUTES: ATTRIBUTES;
  _CATEGORIES: CATEGORY[];
  _WAREHOUSE: { [key: string]: Warehouse } | null;
  _WAREHOUSES: Warehouse[] | null;
  _TENNANTID: string | null;
  _STATUS: PIMItemStatus | null;
  _ts: number;
  _SEO: SEO;
  _ERP: ERP;
  _rid?: string | null;
  _self?: string | null;
  _etag?: string | null;
  _attachments?: string | null;
}



interface ERP {
  ItemType: string | null;
  ItemCode: string | null;
  ProductLine: string | null;
  BinLocation: string | null;
  LotSerialNo: string | null;
  IsERPApproved: boolean | null;
  IsSageItem: boolean | null;
  CountedQty: number | null;
  CountedWhse: string | null;
}

interface SEO {
  title: string | null;
  description: string | null;
  keywords: string | null;
}

interface WAREHOUSE extends Record<string, Warehouse> { }

export interface Warehouse {
  ItemCode: string;
  WarehouseCode: string;
  BinLocation: string;
  ReorderMethod: string;
  LastPhysicalCountDate: string;
  EconomicOrderQty: number;
  ReorderPointQty: number;
  MinimumOrderQty: number;
  MaximumOnHandQty: number;
  QuantityOnHand: number;
  QuantityOnPurchaseOrder: number;
  QuantityOnSalesOrder: number;
  QuantityOnBackOrder: number;
  AverageCost: number;
  QuantityOnWorkOrder: number;
  QuantityRequiredForWO: number;
  QuantityInShipping: number;
  TotalWarehouseValue: number;
  CostCalcQtyCommitted: number;
  CostCalcCostCommitted: number;
  QuantityAvailable: number;
  DateCreated: string;
  TimeCreated: string;
  UserCreatedKey: string;
  DateUpdated: string;
  TimeUpdated: string;
  UserUpdatedKey: string;
  WarehouseName: string;
  WarehouseAddress1: string;
  WarehouseAddress2: string;
  WarehouseCity: string;
  WarehouseState: string;
  WarehouseZipCode: string;
  WarehouseCountryCode: string;
}

export interface WarehouseDirectoryEntry {
  Code: string,
  Name: string,
  Display: string,
  LocationAbbr: string | null
}

export interface CategoryThumbnail {
  CategoryId: number;
  CategoryName: string;
  CategorySlug: string;
  TotalValue: number;
  TotalItems?: number;
}

export interface CategoryInfo {
  Id: number;
  Name: string;
  children: CategoryNode[];
  Slug?: string;
  isRoot?: boolean;
  Path?: string;
  RootCategoryId?: number;
  RootCategorySlug?: string;
}

export interface CategoryNode extends CategoryInfo {
  children: CategoryNode[];
  itemCount?: number;
}

export interface CATEGORY extends CategoryInfo {
  ParentId: number;
  Slug: string;
  RootCategoryId: number;
  RootCategorySlug: string;
  isRoot: boolean;
  Path: string;
}

interface ATTRIBUTES extends Record<string, ItemAttribute> { }

interface ItemAttribute {
  Id: number;
  ItemId: number;
  ItemAttributeValueId: number;
  AttributeValueId: number;
  AttributeId: number;
  Name: string;
  ValueType: number;
  UnitOfMeasureId: null;
  UnitOfMeasureSymbol: null;
  Value: string | number | boolean;
}

export interface Attribute {
  Id?: string | number;
  CategoryId: number;
  AttributeId: number;
  AttributeName: string;
  AttributeType?: any;
  AttributeValueType: number;
  // Measurement metadata, present only on typed attributes (ValueType 1).
  Dimension?: string | null;
  BaseUnitOfMeasureId?: number | null;
  DisplayUnitOfMeasureId?: number | null;
  Decimals?: number | null;
  Prefix?: string;
  Suffix?: string;
  SupportsEstimated?: boolean;
}

export interface AttributeValue {
  Id: number;
  AttributeId: number;
  Value: string;
  ValueType: number;
  UnitOfMeasureId: number | null;
  UnitOfMeasureSymbol: string | null;
  IsDeleted: boolean | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
}

interface IMAGE {
  Id: number;
  ItemId: number;
  Url: string;
  Priority: number;
  Type: string;
  Ext: null | number;
  BlobPath: null;
  Subtype: null;
}